/* 系統數據 List */
import { Fragment } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  Backdrop,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack, Tooltip,
  Typography,
  Skeleton,
  ButtonBase,
} from '@mui/material';
import SimPagination from '../pagination';
import { TypographyLink } from '../hovers';
import { NoData } from '../error';
import { LoadingScreen } from '../loading-screen';
import { fDate } from '../../utils/formatTime';
import { COMMENT_FLAG, READ_FLAG } from '../../store/types';
import TagItem from '../TagItem';

// ================================|| Notification List Item ||================================ //
const NotificationItem = ({ theme, item = {}, onClick }) => {
  const { readFlag, titile, sendTime, isRead } = item || {}
  return (
    <ListItem
      alignItems="center"
      disablePadding
      sx={{ ...theme.typography.hoverPrimaryLeftline2px }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          py: 2,
          px: 1.5,
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
          '& .MuiListItemText-root': {
            m: 0,
            position: 'relative',
            overflow: 'hidden'
          },
          '& .Sim-more': {
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            opacity: 0,
            visibility: 'hidden',
            pl: 3,
            transition: 'all 0.3s 0s ease-out',
          },
          '&:hover': {
            backgroundColor: theme => theme.palette.action.hover,
            '& .Sim-more': {
              opacity: 1,
              visibility: 'visible',
              animation: 'fadeInRight .3s forwards',
            }
          }
        }}
      >
        <ListItemText
          disableTypography
          sx={{ overflow: 'visible !important' }}
          primary={
            <Box component={'span'} display={'flex'} alignItems={'center'} gap={1} mb={1}>
              {
                [READ_FLAG.Unread].includes(Number(readFlag)) && (
                  <Divider sx={{ width: 6, height: 6, alignSelf: 'center', border: 'none', borderRadius: 99 }} color={'error.main'} orientation="vertical" variant="middle" flexItem />
                )
              }
              <Typography
                mr={0.5}
                display={'block'}
                component="span"
                variant="body1"
                fontWeight={[READ_FLAG.Unread].includes(readFlag) ? 700 : 400}
                noWrap
              >
                {titile}
              </Typography>
            </Box>
          }
          secondary={
            <>
              <Typography
                component="span"
                variant={'body2'}
                color={'text.secondary'}
                display={'block'} 
                noWrap
              >
                {sendTime && fDate(sendTime, 'yyyy/MM/dd')}
              </Typography>
              {/* {sender && (<TagItem label={sender} />)} */}
            </>
          }
        />
        <Box className={'Sim-more'}>
          <TypographyLink className={'Sim-icon-link'} primary={''} bgColor={'transparent'} textColor={'#489CFF'} iconColor={'#489CFF'} disableBoxShadow />
        </Box>
      </ListItemButton>
    </ListItem>
  )
};

// ================================|| Notification List Item ||================================ //
const NoticeItem = ({ theme, item = {}, onClick }) => {
  const { readFlag, messageTitle, createTime, isRead, messageType_dictText, messageContent } = item || {}
  return (
    <ListItem
      alignItems="center"
      disablePadding
      sx={{ ...theme.typography.hoverPrimaryLeftline2px }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          py: 2,
          px: 1.5,
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
          '& .MuiListItemText-root': {
            m: 0,
            position: 'relative',
            overflow: 'hidden'
          },
          '& .Sim-more': {
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            opacity: 0,
            visibility: 'hidden',
            pl: 3,
            transition: 'all 0.3s 0s ease-out',
          },
          '&:hover': {
            backgroundColor: theme => theme.palette.action.hover,
            '& .Sim-more': {
              opacity: 1,
              visibility: 'visible',
              animation: 'fadeInRight .3s forwards',
            }
          }
        }}
      >
        <ListItemText
          disableTypography
          sx={{ overflow: 'visible !important' }}
          primary={
            <Box component={'span'} display={'flex'} alignItems={'center'} gap={1} mb={1}>
              {
                [READ_FLAG.Unread].includes(Number(isRead)) && (
                  <Divider sx={{ width: 6, height: 6, alignSelf: 'center', border: 'none', borderRadius: 99 }} color={'error.main'} orientation="vertical" variant="middle" flexItem />
                )
              }
              <Typography
                mr={0.5}
                display={'block'}
                component="span"
                variant="body1"
                fontWeight={[READ_FLAG.Unread].includes(isRead) ? 700 : 400}
                noWrap
              >
                {messageTitle}
              </Typography>
            </Box>
          }
          secondary={
            <Stack direction={'row'}>
              <Typography
                component="span"
                variant={'body2'}
                color={'text.secondary'}
                display={'block'} 
                noWrap
              >
                {createTime && fDate(createTime, 'yyyy/MM/dd')}
              </Typography>
              {
                messageType_dictText && (
                  <Divider sx={{ width: 3, height: 3, mx: 1, my: 0, alignSelf: 'center', border: 'none', backgroundColor: 'color.secondaryGreen1' }} orientation="vertical" variant="middle" flexItem />
                )
              }
              <Typography
                component="span"
                variant={'body2'}
                color={'text.secondary'}
                display={'block'} 
                noWrap
              >
                {messageType_dictText}
              </Typography>
            </Stack>
          }
        />
        <Box className={'Sim-more'}>
          <TypographyLink className={'Sim-icon-link'} primary={''} bgColor={'transparent'} textColor={'#489CFF'} iconColor={'#489CFF'} disableBoxShadow />
        </Box>
      </ListItemButton>
    </ListItem>
  )
};

// ================================|| FeedbackItem List Item ||================================ //
const FeedbackItem = ({ theme, item = {}, onClick }) => {
  const { sender, messageTitle, isRead, createTime, messageStatus } = item || {}
  return (
    <ListItem
      disablePadding
      sx={{ ...theme.typography.hoverPrimaryLeftline2px }}>
      <ListItemButton
        onClick={onClick}
        sx={{
          py: 2,
          px: 1.5,
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
          '& .MuiListItemText-root': {
            m: 0,
            position: 'relative',
            overflow: 'hidden'
          },
          '& .Sim-more': {
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            opacity: 0,
            visibility: 'hidden',
            pl: 3,
            transition: 'all 0.3s 0s ease-out',
          },
          '&:hover': {
            backgroundColor: theme => theme.palette.action.hover,
            '& .Sim-more': {
              opacity: 1,
              visibility: 'visible',
              animation: 'fadeInRight .3s forwards',
            }
          }
        }}
      >
        <ListItemText
          disableTypography
          sx={{ overflow: 'visible !important' }}
          primary={
            <Box component={'span'} display={'flex'} alignItems={'center'} gap={1} mb={1}>
              {
                [READ_FLAG.Unread].includes(Number(isRead)) && (
                  <Divider sx={{ width: 6, height: 6, alignSelf: 'center', border: 'none', borderRadius: 99 }} color={'error.main'} orientation="vertical" variant="middle" flexItem />
                )
              }
              <Typography
                mr={0.5}
                display={'block'}
                component="span"
                variant="body1"
                fontWeight={[READ_FLAG.Unread].includes(Number(isRead)) ? 700 : 400}
                noWrap
              >
                {messageTitle}
              </Typography>
            </Box>
          }
          secondary={
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <ButtonBase 
                disableRipple
                sx={{
                  borderRadius: '4px',
                  padding: theme.spacing(0.5, 1),
                  color: [COMMENT_FLAG.Unreply].includes(Number(messageStatus)) ? 'grey.600' : 'color.primaryMain',
                  backgroundColor: [COMMENT_FLAG.Unreply].includes(Number(messageStatus)) ? 'background.disabled' : '#F0F4FF',
                }}
              >
                
                { [COMMENT_FLAG.Unreply].includes(Number(messageStatus)) ? '未回復' : '已回覆' }
              </ButtonBase>
              <Typography
                component="span"
                variant={'body2'}
                color={'text.secondary'}
                display={'block'} 
                noWrap
              >
                {createTime && fDate(createTime, 'yyyy/MM/dd')}
              </Typography>
              {sender && (<TagItem label={sender} />)}
            </Stack>
          }
        />
        <Box className={'Sim-more'}>
          <TypographyLink className={'Sim-icon-link'} primary={''} bgColor={'transparent'} textColor={'#489CFF'} iconColor={'#489CFF'} disableBoxShadow />
        </Box>
      </ListItemButton>
    </ListItem>
  )
};



// ================================|| Service List ||================================ //
const SimSystemList = ({
  variant = 'notification',
  list = [],
  page = 1,
  defaultPage = 1,
  rowsPerPage = 10,
  total = 0,
  count = 0,
  onChangePage,
  onChangeRowsPerPage,
  onLink,
  loading = false,
  itemMinHeight = 72,
  disabledRowsEmpty = false,
  hideHeader = false,
  hideJumper = false,
  hideRowsPerPage = false,
  filterTools
})=> {
  const theme = useTheme();

  const row = (item) => {
    return {
      notification: <NotificationItem theme={theme} item={item} onClick={(event) => onLink(event, item)}  />,
      notice:       <NoticeItem theme={theme} item={item} onClick={(event) => onLink(event, item)}  />,
      feedback:     <FeedbackItem theme={theme} item={item} onClick={(event) => onLink(event, item)}  />,
    }
  };

  // 页码跳转
  const onQuickJumper = async (event) => {
    if(event?.target?.value && event?.target?.value > 0 && event?.target?.value <= count) {
      await onChangePage(event, Number(event.target.value));
    }
    if(event?.target?.value && event?.target?.value > 0 && event?.target?.value > count) {
      await onChangePage(event, Number(count));
    }
  };

  const emptyRows = list?.length < Number(rowsPerPage) ? Math.floor(10 - list?.length) : 0;

  return (
    <Stack spacing={3}>
      {
        (!hideHeader) && (
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant='h6' minHeight={40} display={'flex'} alignItems={'center'}>
              共&nbsp;
              <Typography variant='h6' color={theme.palette.action.selected} component={'span'}>{total}</Typography> 
              &nbsp;筆相關資料
            </Typography>
            {filterTools}
          </Stack>
        )
      }
      <Stack>
        <Box minHeight={itemMinHeight * Number(rowsPerPage)} position={'relative'}>
          <Backdrop
            sx={{
              position: 'absolute',
              textAlign: 'center',
              height: '100%',
              zIndex: theme.zIndex.drawer + 1,
              backgroundColor: 'transparent'
            }}
            open={Boolean(loading)}
          >
            <LoadingScreen sx={{ alignItems: 'flex-start', minHeight: 'auto' }} />
          </Backdrop>

          {
            total > 0 ? (
              <List disablePadding>
                {
                  _.map(list, (item, index) => {
                    return (
                      <Fragment key={index}>
                        {row(item)[variant]}
                        <Divider component="li" sx={{ mt: -0.125 }} />
                      </Fragment>
                    )
                  })
                }
                {emptyRows > 0 && (!disabledRowsEmpty) && (
                  <ListItem style={{ height: itemMinHeight * emptyRows }} />
                )}
              </List>
            ) : (
              loading ? (
                _.map(Array.from({ length: Number(rowsPerPage) }), (item, index) => {
                  return (
                    <ListItem key={index} sx={{ mb: 0.5 }} disablePadding disableGutters>
                      <Skeleton variant="rectangular" width={`100%`} height={itemMinHeight} />
                    </ListItem>)
                })
              ) : (
                <NoData minHeight={300} />
              )
            )
          }
        </Box>
        <SimPagination
          hideRowsPerPage={hideRowsPerPage}
          disableScrollTop
          hideJumper={hideJumper}
          total={total}
          page={page}
          defaultPage={defaultPage}
          rowsPerPage={rowsPerPage}
          count={count}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onQuickJumper={onQuickJumper}
        />
      </Stack>
    </Stack>
  )
};
SimSystemList.propTypes = {
  variant: PropTypes.oneOf(['notification', 'notice', 'feedback']),
  list: PropTypes.array,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowsPerPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  limit: PropTypes.number,

  onClick: PropTypes.func,
  loading: PropTypes.bool,
};

export default SimSystemList
